import { useMemo } from "react";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import type { ClinicalInsightDateRange } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { AnalysisCardId } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { LoadPhase } from "src-next/modules/clinical-insights/loadPhase";
import {
  deriveInsightLoadPhase,
  useCachedInsightPayload,
  useReportInsightLoadPhase,
} from "src-next/modules/clinical-insights/hooks/useCachedInsightPayload";
import { useInsightPatientHydration } from "src-next/modules/clinical-insights/hooks/useInsightPatientHydration";

interface QrEntriesPayload {
  entries: unknown[];
}

interface UseQrClinicalRegisterAnalysisArgs<T> {
  analysisId: AnalysisCardId;
  author: string;
  practitionerFilter: string;
  dateRange?: ClinicalInsightDateRange;
  getPractitionerName: (ref: string) => string;
  onLoadPhaseChange?: (phase: LoadPhase) => void;
  processEntries: (entries: unknown[], getPractitionerName: (ref: string) => string) => T[];
  getPatientIds: (records: T[]) => string[];
  /** Card visitado: busca e mantém o resultado em memória. */
  fetchEnabled?: boolean;
  /** Card selecionado: só ele sincroniza a fase de carga da página. */
  isActive?: boolean;
}

export function useQrClinicalRegisterAnalysis<T>({
  analysisId,
  author,
  practitionerFilter,
  dateRange,
  getPractitionerName,
  onLoadPhaseChange,
  processEntries,
  getPatientIds,
  fetchEnabled = true,
  isActive = true,
}: UseQrClinicalRegisterAnalysisArgs<T>) {
  const { data, isLoading, error, refresh } = useCachedInsightPayload<QrEntriesPayload>({
    analysisId,
    organizationId: author,
    dateRange,
    practitionerFilter,
    enabled: fetchEnabled && Boolean(author),
    fetcher: async () => {
      const response = await GetQuestionnaireResponse({
        author,
        practitioner: practitionerFilter,
        hasProtocols: false,
        questionnaire: "Questionnaire/clinicalRegister",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });
      return { entries: response?.entry || [] };
    },
  });

  const records = useMemo(
    () => (data ? processEntries(data.entries, getPractitionerName) : []),
    [data, processEntries, getPractitionerName],
  );

  const patientIds = useMemo(
    () => (data ? getPatientIds(records) : []),
    [data, getPatientIds, records],
  );

  const hydration = useInsightPatientHydration(patientIds, Boolean(data));

  const loadPhase = deriveInsightLoadPhase({
    enabled: fetchEnabled && Boolean(author),
    isLoading,
    error,
    hasPayload: data !== undefined,
    patientsLoading: hydration.isLoading,
  });

  useReportInsightLoadPhase(isActive, loadPhase, onLoadPhaseChange);

  return {
    records,
    patients: hydration.patientMap,
    loadPhase,
    patientsProgress: hydration.progress,
    hasData: data !== undefined,
    retry: () => {
      void refresh();
    },
  };
}
